import React from "react"
import { convertValueToReadable } from "services/units/valueConversion"
import classNames from "classnames"
import ConditionalLink from "components/widgets/ConditionalLink"
import SdgIcon from "assets/images/sdg"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { round } from "services/units/round"

const Cell = ({ pillarId, score, cell, isDisabled, quantitative }) => {
  const {
    sdg,
    maturity_field,
    quantitative_field,
    quantitative_field_v2,
    quantitative_text,
    quantitative_text_key,
    quantitative_text_v2,
    quantitative_text_v2_key,
    unit,
    units,
    take_more_space,
    unitType,
  } = cell
  const { t } = useTranslation()
  const useV2QuantitativeField =
    quantitative_field_v2 &&
    (score?.odd12_kpi_methodology_version ?? 1) >= 2
  const resolvedQuantitativeField = useV2QuantitativeField
    ? quantitative_field_v2
    : quantitative_field
  const showQuantitativeTextLabel = useV2QuantitativeField
    ? quantitative_text_v2 ?? quantitative_text
    : quantitative_text
  const quantitativeTextTranslationKey = useV2QuantitativeField
    ? quantitative_text_v2_key ??
      quantitative_text_key ??
      `score:score.${sdg}.quantitative_text_v2`
    : quantitative_text_key ?? `score:score.${sdg}.quantitative_text`
  const sdgScore = score ? score[maturity_field] ?? 0 : 0
  const quantitativeValue = score ? score[resolvedQuantitativeField] ?? 0 : 0

  const quantitativeFieldConverted = convertValueToReadable(
    quantitativeValue,
    unit,
    units
  )

  let unitText = quantitativeFieldConverted.unit

  if (unitType) {
    unitText = t(`units:units.${unitType}.${quantitativeFieldConverted.unit}`)
  }

  return (
    <li
      key={sdg}
      className={classNames({
        colored: true,
        background: true,
        disabled: isDisabled,
      })}
    >
      <ConditionalLink to={`/score/${pillarId}/${sdg}`} condition={!isDisabled}>
        <div className="progress progress--full colored" />
        <div
          className="progress colored"
          style={{ height: `${sdgScore}%` }}
        ></div>
        <SdgIcon id={sdg} quantitative={quantitative && !isDisabled} />
        {quantitative && !isDisabled && (
          <div
            className={classNames("quantitative", {
              xl: take_more_space,
            })}
          >
            <p>
              {`${round(quantitativeFieldConverted.value)}${
                unitText ? ` ${unitText}` : ""
              }`}
            </p>
            {showQuantitativeTextLabel && (
              <p>{t(quantitativeTextTranslationKey)}</p>
            )}
          </div>
        )}
      </ConditionalLink>
    </li>
  )
}

Cell.propTypes = {
  pillarId: PropTypes.string.isRequired,
  score: PropTypes.object.isRequired,
  cell: PropTypes.shape({
    sdg: PropTypes.number.isRequired,
    maturity_field: PropTypes.string.isRequired,
    quantitative_field: PropTypes.string.isRequired,
    quantitative_field_v2: PropTypes.string,
    quantitative_text: PropTypes.bool,
    quantitative_text_key: PropTypes.string,
    quantitative_text_v2: PropTypes.bool,
    quantitative_text_v2_key: PropTypes.string,
    unit: PropTypes.string,
    units: PropTypes.arrayOf(PropTypes.string),
    take_more_space: PropTypes.bool,
    unitType: PropTypes.string,
  }).isRequired,
  isDisabled: PropTypes.bool,
  quantitative: PropTypes.bool,
}

Cell.defaultProps = {
  isDisabled: false,
  quantitative: false,
}

export default Cell
